import * as styles from "./Card.css";

import { animate } from "animejs";
import { clsx } from "clsx";
import { useContext, useEffect, useRef } from "react";
import type { CSSProperties, JSX } from "react";

import { Panel } from "@frontend/components/atoms/Panel";
import { vars } from "@frontend/styles";
import { TRANSITION_NORMAL_MS } from "@frontend/styles/constants";
import { mergeRefs } from "@frontend/utils/mergeRefs";

import type { CardProps } from "./Card.types";
import { CardGroupContext } from "./CardGroup.context";
import {
  CARD_DEFAULT_BLUR,
  CARD_DEFAULT_BORDER,
  CARD_DEFAULT_BORDER_WIDTH,
  CARD_DEFAULT_GLOW,
  CARD_DEFAULT_INTENSITY,
  CARD_DEFAULT_SIZE,
} from "./constants";
import { glowOpacity, paintGlowElement } from "./glow-render";

const HOVER_TRANSITION_MS = 300;
const PULSE_HALF_DURATION_MS = 1000;

export function Card({
  glow = CARD_DEFAULT_GLOW,
  border = CARD_DEFAULT_BORDER,
  disabled = false,
  color = `${vars.color.primary}`,
  size = CARD_DEFAULT_SIZE,
  radius,
  blur = CARD_DEFAULT_BLUR,
  borderWidth = CARD_DEFAULT_BORDER_WIDTH,
  intensity = CARD_DEFAULT_INTENSITY,
  className,
  contentClassName,
  style,
  children,
  ref,
  ...props
}: CardProps): JSX.Element {
  const insideGroup = useContext(CardGroupContext);

  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef({ x: 0.5, y: 0.5 });

  // Read by the pointermove handler below without re-subscribing it
  const renderConfigRef = useRef({ glow, color, size });

  useEffect(() => {
    renderConfigRef.current = { glow, color, size };
  });

  function paintBackground() {
    const glowEl = glowRef.current;

    if (!glowEl) {
      return;
    }

    const { x, y } = pointerRef.current;
    const {
      glow: currentGlow,
      color: currentColor,
      size: currentSize,
    } = renderConfigRef.current;

    paintGlowElement(glowEl, currentGlow, {
      x,
      y,
      color: currentColor,
      size: currentSize,
    });
  }

  // Repaints immediately when the static config changes
  useEffect(() => {
    paintBackground();
  }, [glow, color, size]);

  useEffect(() => {
    if (insideGroup) {
      return;
    }

    const el = cardRef.current;

    if (!el) {
      return;
    }

    function handlePointerMove(event: PointerEvent) {
      if (disabled || !el) {
        return;
      }

      const rect = el.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      // Retargets the in-flight tween instead of snapping, so the glow
      // eases toward the pointer instead of jumping to it on every move.
      animate(pointerRef.current, {
        x,
        y,
        duration: TRANSITION_NORMAL_MS,
        ease: "outQuad",
        onUpdate: paintBackground,
      });
    }

    function handlePointerEnter() {
      if (!disabled && glowRef.current) {
        animate(glowRef.current, {
          opacity: glowOpacity(glow, 1, intensity),
          duration: HOVER_TRANSITION_MS,
          ease: "outQuad",
        });
      }
    }

    function handlePointerLeave() {
      if (glowRef.current) {
        animate(glowRef.current, {
          opacity: 0,
          duration: HOVER_TRANSITION_MS,
          ease: "outQuad",
        });
      }
    }

    el.addEventListener("pointermove", handlePointerMove, { passive: true });
    el.addEventListener("pointerenter", handlePointerEnter, {
      passive: true,
    });
    el.addEventListener("pointerleave", handlePointerLeave, {
      passive: true,
    });

    return () => {
      el.removeEventListener("pointermove", handlePointerMove);
      el.removeEventListener("pointerenter", handlePointerEnter);
      el.removeEventListener("pointerleave", handlePointerLeave);
    };
  }, [disabled, glow, intensity, insideGroup]);

  useEffect(() => {
    if (disabled && glowRef.current) {
      animate(glowRef.current, { opacity: 0, duration: HOVER_TRANSITION_MS });
    }
  }, [disabled]);

  useEffect(() => {
    const glowEl = glowRef.current;

    if (!glowEl || glow !== "pulse") {
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const pulse = animate(glowEl, {
      filter: ["brightness(1)", "brightness(1.5)"],
      duration: PULSE_HALF_DURATION_MS,
      ease: "inOutSine",
      loop: true,
      alternate: true,
    });

    return () => {
      pulse.cancel();
      glowEl.style.filter = "";
    };
  }, [glow]);

  const glowLayer = (
    <div
      ref={glowRef}
      data-slot="card-glow"
      data-glow={glow}
      data-color={color}
      data-size={size}
      data-intensity={intensity}
      className={styles.glowEffect({ glow, frame: border })}
      aria-hidden="true"
      style={
        {
          borderWidth: `${borderWidth}px`,
          filter: glow === "background" ? `blur(${blur}px)` : undefined,
        } as CSSProperties
      }
    />
  );

  if (border !== "solid") {
    return (
      <div
        data-slot="card"
        data-disabled={disabled || undefined}
        ref={mergeRefs(cardRef, ref)}
        className={clsx(styles.ringedRoot, className)}
        style={style}
        {...props}
      >
        <Panel
          border={border}
          width="fill"
          contentClassName={clsx(styles.panelContent, contentClassName)}
        >
          {children}
        </Panel>
        {glowLayer}
      </div>
    );
  }

  return (
    <div
      data-slot="card"
      data-disabled={disabled || undefined}
      ref={mergeRefs(cardRef, ref)}
      className={clsx(styles.root, className)}
      style={{
        borderRadius: radius !== undefined ? `${radius}px` : undefined,
        ...style,
      }}
      {...props}
    >
      <div className={clsx(styles.content, contentClassName)}>{children}</div>
      {glowLayer}
    </div>
  );
}
